import axios from "axios";
import { useAuthStore } from "../stores/auth";
import { loginUrlWithNext } from "../lib/safe-redirect";

const apiClient = axios.create({
  // Use the admin origin by default. In production nginx proxies /api to the
  // API container; an explicit VITE_API_URL is only needed for split domains.
  baseURL: import.meta.env.VITE_API_URL || "",
  headers: { "Content-Type": "application/json" },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Oturum yenilenemedi: giriş sayfasına git, ama bulunulan sayfayı ?next= ile
// koru ki tekrar girişte kullanıcı kaldığı yere dönsün. `loginUrl`, istek
// başında (refresh denemesinden önce) hesaplanır: refreshAccessToken() hata
// durumunda store'u kendisi sıfırlar ve ProtectedRoute adresi /login'e
// çevirebilir; o anki adres artık geri dönülecek sayfa olmaz.
function redirectToLogin(loginUrl: string, wasOnLogin: boolean) {
  useAuthStore.getState().logout();
  if (!wasOnLogin) window.location.href = loginUrl;
}

apiClient.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthRequest =
      originalRequest?.url?.includes("/api/auth/login") ||
      originalRequest?.url?.includes("/api/auth/refresh");

    if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      const wasOnLogin = window.location.pathname === "/login";
      const loginUrl = loginUrlWithNext(window.location);

      try {
        const newToken = await useAuthStore.getState().refreshAccessToken();
        if (newToken) {
          processQueue(null, newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        } else {
          processQueue(error, null);
          redirectToLogin(loginUrl, wasOnLogin);
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Refresh'e ulaşılamadı (ağ/5xx): oturum geçerli olabilir, çıkış yaptırma.
        processQueue(refreshError, null);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
